import React, {useState} from 'react';
import {Box, Typography, Accordion, AccordionSummary, Button} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

interface MotifReviewProps {
    formData: MotifFormData;
    handleNext: (stepIndex: number) => void;
    dbaAnalyses: DbaMotifAnalysisOption[];
}

const MotifReview: React.FC<MotifReviewProps> = ({formData, handleNext, dbaAnalyses}) => {
    const selectedDba = dbaAnalyses.find(analysis => analysis.analysis_id === formData.differential_binding_analysis_id);
    const includedDirections =
        selectedDba?.inputs
            .filter(input => input.motif_available)
            .map(input => `${input.comparison} enriched in ${input.enrichment_direction}`)
            .join('\n') ?? '';

    const userSetMotifAnalysisConfigurations = [
        {
            category: 'Analysis Details',
            parameters: [
                {name: 'Analysis Name', value: formData.name},
                {name: 'Description', value: formData.description},
                // selectedDba will always be defined if this component is reached
                {name: 'Differential Binding Analysis', value: selectedDba?.analysis_name || ''},
                {name: 'Included Directions', value: includedDirections}
            ]
        },
        {
            category: 'Settings',
            parameters: [
                {
                    name: 'Parameters for Motif Analysis',
                    values: [
                        {
                            name: 'Enrichment Test',
                            value: formData.motif_analysis.ame_method
                        },
                        {
                            name: 'Number of Motifs',
                            value: formData.motif_analysis.nmotifs
                        },
                        {
                            name: 'E Value Cutoff',
                            value: formData.motif_analysis.e_val
                        }
                    ]
                }
            ]
        }
    ];

    const [listStates, setListStates] = useState(new Array(userSetMotifAnalysisConfigurations.length).fill({open: true, editMode: false}));

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap="36px"
            marginBottom="24px"
            bgcolor={'surface-container-lowest'}
            p={3}
            borderRadius={'12px'}>
            <Box borderRadius="20px" border="1px solid" borderColor="outline-variant" overflow="hidden">
                <Accordion
                    expanded={listStates[0].open}
                    sx={{
                        backgroundColor: 'unset',
                        boxShadow: 'unset',
                        '& .MuiAccordionSummary-root, .MuiAccordionSummary-root.Mui-expanded': {
                            minHeight: '0'
                        },
                        '& .MuiAccordionSummary-root.Mui-expanded': {
                            borderBottom: '1px solid',
                            borderColor: 'outline-variant'
                        }
                    }}>
                    <AccordionSummary
                        aria-controls="panel1d-content"
                        expandIcon={<ArrowDropUpIcon />}
                        id="panel1d-header"
                        onClick={() =>
                            setListStates(state =>
                                state.map((item, index) => {
                                    if (index === 0) {
                                        return {...item, open: !item.open};
                                    }
                                    return item;
                                })
                            )
                        }
                        sx={{
                            backgroundColor: 'surface-container-high',
                            padding: '4px 4px 4px 16px',
                            height: '0',
                            minHeight: '0',
                            gap: '12px',
                            flexDirection: 'row-reverse',
                            alignItems: 'center',
                            '& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': {
                                alignItems: 'center',
                                margin: 0
                            },
                            '& .MuiAccordionSummary-root.Mui-expanded': {}
                        }}>
                        <Typography variant="title" size="medium" width="100%">
                            {userSetMotifAnalysisConfigurations[0].category}
                        </Typography>
                        <Button
                            startIcon={<EditOutlinedIcon />}
                            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                e.stopPropagation();
                                handleNext(0);
                            }}>
                            Edit
                        </Button>
                    </AccordionSummary>
                    <Box px={5} paddingTop={3} pb={3}>
                        {userSetMotifAnalysisConfigurations[0].parameters.map((param: any) => (
                            <Box display="flex" gap={10} mb={1} key={param.name}>
                                <Typography variant="title" size="medium" fontWeight={600} width={200}>
                                    {param.name}
                                </Typography>
                                <Typography variant="body" size="large" width={350} whiteSpace="pre-line">
                                    {param.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Accordion>
            </Box>

            <Box borderRadius="20px" border="1px solid" borderColor="outline-variant" overflow="hidden">
                <Accordion
                    expanded={listStates[1].open}
                    sx={{
                        backgroundColor: 'unset',
                        boxShadow: 'unset',
                        '& .MuiAccordionSummary-root, .MuiAccordionSummary-root.Mui-expanded': {
                            minHeight: '0'
                        },
                        '& .MuiAccordionSummary-root.Mui-expanded': {
                            borderBottom: '1px solid',
                            borderColor: 'outline-variant'
                        }
                    }}>
                    <AccordionSummary
                        aria-controls="panel1d-content"
                        expandIcon={<ArrowDropUpIcon />}
                        id="panel1d-header"
                        onClick={() =>
                            setListStates(state =>
                                state.map((item, index) => {
                                    if (index === 1) {
                                        return {...item, open: !item.open};
                                    }
                                    return item;
                                })
                            )
                        }
                        sx={{
                            backgroundColor: 'surface-container-high',
                            padding: '4px 4px 4px 16px',
                            height: '0',
                            minHeight: '0',
                            gap: '12px',
                            flexDirection: 'row-reverse',
                            alignItems: 'center',
                            '& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': {
                                alignItems: 'center',
                                margin: 0
                            },
                            '& .MuiAccordionSummary-root.Mui-expanded': {}
                        }}>
                        <Typography variant="title" size="medium" width="100%">
                            {userSetMotifAnalysisConfigurations[1].category}
                        </Typography>
                        <Button
                            startIcon={<EditOutlinedIcon />}
                            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                e.stopPropagation();
                                handleNext(1);
                            }}>
                            Edit
                        </Button>
                    </AccordionSummary>
                    <Box px={5} paddingTop={3} pb={3}>
                        {userSetMotifAnalysisConfigurations[1].parameters.map((param: any) => {
                            return (
                                <div key={param.name}>
                                    {param.values.map((item: any) => {
                                        return (
                                            <Box display="flex" gap={10} mb={1} key={item.name}>
                                                <Typography variant="title" size="medium" fontWeight={600} width={200}>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="body" size="large" width={350}>
                                                    {item.value}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </Box>
                </Accordion>
            </Box>
        </Box>
    );
};

export default MotifReview;
